import { zodResolver } from '@hookform/resolvers/zod';
import { CaretLeftIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/common/button';
import { useConfirm } from '@/components/feedback/dialog';
import { useToast } from '@/components/feedback/toast';
import { FormField, RadioGroupField, TextAreaField } from '@/components/forms';
import { ApiError } from '@/lib/api/errors';
import { dedupeMessages, type ValidationDetail } from '@/lib/validation/server-details';

import { checkDiscountNameAvailability } from '../../api';
import { DISCOUNT_ERROR_CODES } from '../../constants';
import { formatTrigger, toApplicabilitySummary } from '../../format';
import { useCreateDiscount } from '../../hooks';
import {
  DISCOUNT_DESCRIPTION_MAX,
  defaultApplicability,
  defaultQuantityRule,
  defaultTrigger,
  discountCreateSchema,
  discountStep1Schema,
  isFirstTimeTriggerForCustomer,
  maskDiscountName,
  maskDiscountValue,
  triggerSchema,
} from '../../schemas';
import { toCreateBody } from '../../serialize';
import type { DiscountFormValues } from '../../types';
import { resolveDiscountDetailField, type NestedApplicabilityScope } from '../../validation-detail';
import { DiscountOverview } from '../discount-overview';

import { ApplicabilityFields } from './applicability-fields';
import styles from './discount-create-form.module.scss';
import { QuantityRuleFields } from './quantity-rule-fields';
import { TriggerFields } from './trigger-fields';

const FORMAT_OPTIONS = [
  { label: 'Flat Amount', value: 'flat_amount' },
  { label: 'Percentage', value: 'percentage' },
];

const STEP_TITLES = ['Discount Details', 'Trigger', 'Quantity Rule'] as const;

export type DiscountCreateFormProps = {
  onExit: () => void;
};

export function DiscountCreateForm({ onExit }: DiscountCreateFormProps) {
  const { confirm } = useConfirm();
  const toast = useToast();

  const form = useForm<DiscountFormValues>({
    resolver: zodResolver(discountCreateSchema),
    mode: 'onTouched',
    // Flat Amount is pre-selected (114745) — the Amount/Percentage field below always renders
    // under one of the two formats, so leaving neither radio selected misrepresented it as Flat.
    defaultValues: {
      name: '',
      discountFormat: 'flat_amount',
      value: '',
      description: '',
      applicability: defaultApplicability('device'),
      trigger: defaultTrigger(),
      quantityRule: defaultQuantityRule(),
    },
  });

  const create = useCreateDiscount();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [checkingName, setCheckingName] = useState(false);
  const [name, discountFormat, value, applicability, trigger, quantityRule] = form.watch([
    'name',
    'discountFormat',
    'value',
    'applicability',
    'trigger',
    'quantityRule',
  ]);
  // Read during render so RHF's proxy subscribes: `isDirty` for handleBack, `errors` so a server
  // 409/422 field error (on a client-valid form) keeps Create disabled until that field is edited.
  const { isDirty, errors } = form.formState;

  // Whole-form validity gates the single Create submit (client-computable rules; grilling Q7).
  const createReady = discountCreateSchema.safeParse(form.getValues()).success;

  const step1Valid = discountStep1Schema.safeParse({
    name,
    discountFormat,
    value,
    description: form.getValues('description'),
    applicability,
  }).success;

  const step2Valid =
    trigger != null &&
    triggerSchema.safeParse(trigger).success &&
    !isFirstTimeTriggerForCustomer(applicability.type, trigger.type);

  // Switching format re-masks the value so an out-of-range amount clamps (e.g. 500 → 100 on %).
  useEffect(() => {
    if (!discountFormat) return;
    const remasked = maskDiscountValue(form.getValues('value'), discountFormat);
    if (remasked !== form.getValues('value')) {
      form.setValue('value', remasked, { shouldValidate: form.formState.isSubmitted });
    }
  }, [discountFormat, form]);

  // Breaking Step-1 change: switching to Customer scope invalidates a First Time Customer trigger —
  // clear it (to null) so Step 2 re-requires a selection with the "Please select a trigger" error
  // (AC). Runs at any step (this form is always mounted, unlike <TriggerFields>).
  //
  // The clear is reversible (117040): if the admin flips back away from Customer before ever
  // re-selecting a Step-2 trigger, the cleared value is restored rather than staying lost — a
  // Step-1-only detour (Device → Customer → Device again, all before Next) shouldn't cost Step 2
  // a selection it never asked to change.
  const clearedTriggerRef = useRef<typeof trigger>(null);
  useEffect(() => {
    if (trigger != null && isFirstTimeTriggerForCustomer(applicability.type, trigger.type)) {
      clearedTriggerRef.current = trigger;
      form.setValue('trigger', null, { shouldValidate: true });
    } else if (
      trigger == null &&
      clearedTriggerRef.current != null &&
      applicability.type !== 'customer'
    ) {
      form.setValue('trigger', clearedTriggerRef.current, { shouldValidate: true });
      clearedTriggerRef.current = null;
    }
  }, [applicability.type, trigger, form]);

  const valueLabel = discountFormat === 'percentage' ? 'Discount Percentage' : 'Discount Amount';

  const handleNext = async () => {
    if (step === 1) {
      const ok = await form.trigger([
        'name',
        'discountFormat',
        'value',
        'description',
        'applicability',
      ]);
      if (!ok) return;
      // The name input stays editable while the async check runs, so capture the name we check and
      // discard a stale result if the user edits it before the response resolves — otherwise we'd
      // advance with a never-checked name, or pin "already exists" onto a name they've replaced.
      const checkedName = form.getValues('name');
      setCheckingName(true);
      try {
        const available = await checkDiscountNameAvailability(checkedName);
        if (form.getValues('name') !== checkedName) return;
        if (!available) {
          form.setError('name', {
            message: 'Discount Name already exists. Please use a different name.',
          });
          return;
        }
        setStep(2);
      } catch {
        toast.error({ title: 'Something went wrong. Please try again.' });
      } finally {
        setCheckingName(false);
      }
      return;
    }
    if (step === 2) {
      const ok = await form.trigger(['trigger']);
      if (!ok) return;
      setStep(3);
    }
  };

  const handleBack = async () => {
    if (isDirty) {
      const ok = await confirm({
        title: 'Discard Discount?',
        subtitle: 'Your changes will not be saved.',
        confirmText: 'Discard',
        cancelText: 'Cancel',
        danger: true,
      });
      if (!ok) return;
    }
    onExit();
  };

  // The wizard's single submit: validate the whole form, POST once, then toast + navigate on success.
  // Errors branch on the contract code (never message text): 409 → name error (Step 1); 422 →
  // inline field errors (name/value → Step 1). The set `value`/`name` error keeps Create disabled
  // until that field is edited (grilling Q7 — price-exceeds surfaces on the Create attempt).
  const onCreate = async () => {
    if (!(await form.trigger())) return;
    try {
      await create.mutateAsync(toCreateBody(form.getValues()));
      toast.success({ title: 'Discount created successfully' });
      onExit();
    } catch (err) {
      if (err instanceof ApiError && err.code === DISCOUNT_ERROR_CODES.NAME_EXISTS) {
        form.setError('name', {
          message: 'Discount Name already exists. Please use a different name.',
        });
        setStep(1);
        return;
      }
      // Both validation codes land here: the 422 domain verdict and the 400 payload one carry the
      // same `details[{field, message}]` shape, so they get the same treatment (ADO 117110).
      if (
        err instanceof ApiError &&
        (err.code === DISCOUNT_ERROR_CODES.VALIDATION_FAILED ||
          err.code === DISCOUNT_ERROR_CODES.PAYLOAD_INVALID)
      ) {
        const details = (Array.isArray(err.details) ? err.details : []) as ValidationDetail[];
        // Announce first, and unconditionally (ADO 114870, reopened by QA). The wizard silently
        // jumping back a step is not a message: the admin needs the server's own reason said out
        // loud whether or not an input could also hold it. The generic envelope string is the last
        // resort, shown only when the response carried no details at all. A stable key so a
        // repeated Create updates one notice instead of stacking a second. Deduped for the same
        // reason the Customer form is: the backend pushes one detail per offending tier, so two
        // rows breaching one Step-1 limit carry the identical string (PR #369 review).
        toast.error({
          title: details.length > 0 ? dedupeMessages(details).join(' ') : err.message,
          key: 'discount-create-error',
        });
        // Then locate: every detail the wizard can render goes onto its own input, and the first
        // one decides which step to land on. Tier rows are addressed by index, so the resolver
        // needs the live count.
        const tierCount =
          quantityRule.type === 'quantity_based' ? (quantityRule.tiers?.length ?? 0) : 0;
        // Which nested picker is on screen, so a scope-blind backend path (the price checks all
        // report `applicability.devices`) lands on the control the Customer arm actually renders.
        const nestedScope: NestedApplicabilityScope =
          applicability.type === 'customer' && applicability.applicableTo.type !== 'all_products'
            ? applicability.applicableTo.type
            : null;
        let firstStep: 1 | 2 | 3 | null = null;
        for (const d of details) {
          const resolved = resolveDiscountDetailField(d.field, tierCount, nestedScope);
          if (!resolved) continue;
          form.setError(resolved.path, { message: d.message });
          firstStep ??= resolved.step;
        }
        if (firstStep !== null) setStep(firstStep);
        return;
      }
      toast.error({ title: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <form className={styles.form} noValidate onSubmit={(e) => e.preventDefault()}>
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          <button type="button" aria-label="Back" className={styles.backBtn} onClick={handleBack}>
            <CaretLeftIcon size={20} weight="bold" />
          </button>
          <h1 className={styles.title}>Create Discount</h1>
        </div>

        <div className={styles.actions}>
          <div className={styles.stepBar}>
            <span className={styles.stepTitle}>{STEP_TITLES[step - 1]}</span>
            <span className={styles.stepCount}>Step {step}/3</span>
            <span className={styles.progressTrack}>
              <span className={styles.progressFill} style={{ width: `${(step / 3) * 100}%` }} />
            </span>
          </div>

          {step > 1 ? (
            <Button
              htmlType="button"
              variant="outline"
              className={styles.pill}
              onClick={() => setStep((s) => (s === 3 ? 2 : 1))}
            >
              Previous
            </Button>
          ) : null}

          {step === 3 ? (
            <Button
              htmlType="button"
              variant="neutral"
              className={styles.pill}
              disabled={!createReady || create.isPending || !!errors.name || !!errors.value}
              loading={create.isPending}
              onClick={onCreate}
            >
              Create
            </Button>
          ) : (
            <Button
              htmlType="button"
              variant="neutral"
              className={styles.pill}
              disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid) || checkingName}
              loading={checkingName}
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </div>
      </header>

      <div className={styles.body}>
        <div
          className={[styles.fields, step === 3 ? styles.fieldsWide : ''].filter(Boolean).join(' ')}
        >
          {step === 1 ? (
            <>
              <FormField name="name" control={form.control} label="Discount Name" required>
                {({ field, fieldState }) => (
                  <Input
                    id="name"
                    placeholder="Enter"
                    status={fieldState.error ? 'error' : undefined}
                    value={field.value}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      field.onChange(maskDiscountName(e.target.value));
                      form.clearErrors('name'); // free Create after a server 409/422 name error
                    }}
                  />
                )}
              </FormField>

              <RadioGroupField
                name="discountFormat"
                control={form.control}
                label="Discount Format"
                required
                direction="horizontal"
                options={FORMAT_OPTIONS}
              />

              <FormField name="value" control={form.control} label={valueLabel} required>
                {({ field, fieldState }) => (
                  <Input
                    id="value"
                    inputMode="decimal"
                    placeholder="Enter"
                    prefix={discountFormat === 'percentage' ? undefined : '$'}
                    suffix={discountFormat === 'percentage' ? '%' : undefined}
                    status={fieldState.error ? 'error' : undefined}
                    value={field.value}
                    ref={field.ref}
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      field.onChange(
                        maskDiscountValue(e.target.value, discountFormat ?? 'flat_amount'),
                      );
                      form.clearErrors('value'); // free Create after a server 422 price-exceeds error
                    }}
                  />
                )}
              </FormField>

              <TextAreaField
                name="description"
                control={form.control}
                label="Discount Description"
                placeholder="Enter description"
                rows={5}
                maxLength={DISCOUNT_DESCRIPTION_MAX}
                showCount
                style={{ resize: 'none' }}
              />

              <ApplicabilityFields form={form} />
            </>
          ) : null}

          {step === 2 ? <TriggerFields form={form} /> : null}

          {step === 3 ? <QuantityRuleFields form={form} /> : null}
        </div>

        <div className={styles.overview}>
          <DiscountOverview
            name={name}
            discountFormat={discountFormat}
            value={value}
            applicability={toApplicabilitySummary(applicability)}
            trigger={trigger ? formatTrigger(trigger) : undefined}
            quantityRule={quantityRule}
          />
        </div>
      </div>
    </form>
  );
}
